const AI_PATTERN = /人工智能|大模型|机器学习|深度学习|\bai\b|\bllm\b|\bagent\b|chatbot|copilot|生成式/i
const FRONTEND_PATTERN = /前端|react|vue|angular|svelte|css|ui|组件库|web 应用/i
const BACKEND_PATTERN = /后端|服务端|api|微服务|spring|django|flask|express|nestjs|数据库/i
const TOOL_PATTERN = /开发工具|效率工具|命令行|cli|编辑器|自动化|脚手架|模板|workflow|devtool/i
const INFRA_PATTERN = /云原生|基础设施|容器|kubernetes|docker|运维|监控|数据库|分布式|网络|存储/i
const MOBILE_PATTERN = /移动端|android|ios|flutter|react native|小程序/i
const SECONDARY_DEV_PATTERN = /二次开发|二开|框架|平台|工具|模板|脚手架|组件库|系统|自托管|self-host/i
const BEGINNER_PATTERN = /新手|初学|入门|教程|示例|学习|练手|从零/i
const ADVANCED_PATTERN = /中高级|有经验|一定基础|较高门槛|复杂|底层|内核|编译器|操作系统/i
const RESUME_PATTERN = /简历|作品集|实战|工程实践|完整项目|求职/i

function asText(value) {
  if (Array.isArray(value)) return value.join(' ')
  return value || ''
}

function repoText(repo) {
  return [
    repo?.fullName,
    repo?.description,
    repo?.summary,
    repo?.reason,
    asText(repo?.technicalHighlights),
    repo?.learningValue,
    repo?.audience,
    ...(repo?.topics || []),
    ...(repo?.tags || []),
  ].filter(Boolean).join(' ')
}

function hasTechnicalContent(repo) {
  return Boolean(repo?.technicalHighlights && repo?.technicalHighlights.length > 12)
}

export function getRepoDisplayTags(repo) {
  const text = repoText(repo)
  const labels = []
  const recommendationIndex = Number(repo?.recommendationIndex || 0)
  const stars = Number(repo?.stars || 0)
  const forks = Number(repo?.forks || 0)
  const starGrowth = Number(repo?.starGrowth || 0)

  if (AI_PATTERN.test(text)) labels.push('AI 工具')
  if (stars >= 10000 || forks >= 1500 || starGrowth >= 300) {
    labels.push('高热度')
  }
  if (
    BEGINNER_PATTERN.test(text)
    || (repo?.learningValue && recommendationIndex >= 60)
  ) {
    labels.push('适合学习')
  }
  if (
    RESUME_PATTERN.test(text)
    || (recommendationIndex >= 78 && hasTechnicalContent(repo))
  ) {
    labels.push('适合简历')
  }
  if (SECONDARY_DEV_PATTERN.test(text) && repo?.licenseName) {
    labels.push('适合二开')
  }
  if (ADVANCED_PATTERN.test(text)) labels.push('新手谨慎')

  const sourceTags = Array.isArray(repo?.tags) ? repo.tags : []
  const fallbackTags = [
    repo?.language ? `${repo.language} 项目` : '开源项目',
    recommendationIndex >= 70 ? '值得关注' : '持续观察',
  ]
  const tags = [...new Set([
    ...labels,
    ...sourceTags,
    ...fallbackTags,
  ].filter(Boolean))]

  return tags.slice(0, 4)
}

export function getRepoDirection(repo) {
  const text = repoText(repo)
  if (AI_PATTERN.test(text)) return 'AI 工具'
  if (TOOL_PATTERN.test(text)) return '开发工具'
  if (FRONTEND_PATTERN.test(text)) return '前端开发'
  if (BACKEND_PATTERN.test(text)) return '后端开发'
  if (INFRA_PATTERN.test(text)) return '数据与基础设施'
  if (MOBILE_PATTERN.test(text)) return '移动开发'
  return repo?.language ? `${repo.language} 生态` : '开源工程'
}

function topValues(values, limit = 3) {
  const counts = new Map()
  values.filter(Boolean).forEach((value) => {
    counts.set(value, (counts.get(value) || 0) + 1)
  })
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, limit)
    .map(([value]) => value)
}

export function summarizeDailyRepos(items = [], total = items.length) {
  const languages = topValues(items.map((repo) => repo.language), 3)
  const keywords = topValues(items.flatMap((repo) => [
    getRepoDirection(repo),
    ...(Array.isArray(repo.tags) ? repo.tags.slice(0, 2) : []),
  ]), 3)

  return {
    total,
    languages,
    keywords,
    learningCount: items.filter((repo) => (
      getRepoDisplayTags(repo).includes('适合学习')
    )).length,
    hasData: items.length > 0,
  }
}

export function getRepoRiskTips(repo) {
  const tips = []
  const labels = getRepoDisplayTags(repo)

  if (!repo?.licenseName) {
    tips.push('许可证信息未明确，二次开发或商用前请先确认授权范围。')
  }
  if (repo?.aiStatus === 'fallback' || repo?.isAiFallback) {
    tips.push('当前解读基于基础项目信息，技术判断请以仓库 README 和源码为准。')
  }
  if (labels.includes('新手谨慎')) {
    tips.push('项目可能有一定上手门槛，建议先确认所需基础、运行环境和依赖成本。')
  }
  if (tips.length === 0) {
    tips.push('实际采用前建议检查 README、Issue、版本发布频率和依赖安全情况。')
  }

  return tips.slice(0, 2)
}

function shareHighlights(repo) {
  const highlights = Array.isArray(repo?.technicalHighlights)
    ? repo.technicalHighlights
    : String(repo?.technicalHighlights || '').split(/[；;\n]/)
  return highlights
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3)
    .join('；')
}

function shareValue(value, fallback) {
  const text = String(value ?? '').trim()
  return text && text !== 'undefined' && text !== 'null' ? text : fallback
}

export function buildRepoShareText(repo, pageUrl) {
  const projectName = shareValue(repo?.fullName || repo?.name, '这个项目')
  const introduction = shareValue(
    repo?.description || repo?.summary,
    '暂无项目简介，可打开详情页继续了解。',
  )
  const highlights = shareHighlights(repo)
    || repo?.reason
    || '建议查看项目详情了解技术特点'
  const suitableFor = repo?.audience
    || getRepoDisplayTags(repo).filter((tag) => tag !== '高热度').join('、')
    || '对该技术方向感兴趣的开发者'
  const heatScore = Number(repo?.heatScore ?? repo?.heat_score ?? 0)
  const detailUrl = shareValue(pageUrl, 'https://www.kkeutopic.top/#/')

  return [
    `今天发现一个值得关注的开源项目：${projectName}`,
    `简介：${introduction}`,
    `技术亮点：${shareValue(highlights, '建议查看项目详情了解技术特点')}`,
    `适合人群：${shareValue(suitableFor, '对该技术方向感兴趣的开发者')}`,
    `综合热度：${Number.isFinite(heatScore) ? heatScore : 0}`,
    `项目详情：${detailUrl}`,
  ].join('\n')
}

export function buildDailyTopShareText(items = [], date, pageUrl) {
  const topItems = items.slice(0, 5)
  const lines = topItems.map((repo, index) => {
    const projectName = shareValue(repo?.fullName || repo?.name, `开源项目 ${index + 1}`)
    const introduction = shareValue(
      repo?.description || repo?.summary,
      '暂无简介，建议打开榜单查看详情。',
    )
    const heatScore = Number(repo?.heatScore ?? repo?.heat_score ?? 0)
    const language = shareValue(repo?.language, '多语言')
    return [
      `${index + 1}. ${projectName}`,
      `   ${introduction}`,
      `   ${language} · 热度 ${Number.isFinite(heatScore) ? heatScore : 0}`,
    ].join('\n')
  })

  return [
    `GitHub 趋势 | ${shareValue(date, '今日')} Top ${topItems.length}`,
    '',
    ...lines.flatMap((line) => [line, '']),
    '每天自动筛选值得学习、收藏、二开和写进简历的 GitHub 开源项目。',
    `查看完整榜单：${shareValue(pageUrl, 'https://www.kkeutopic.top/#/')}`,
  ].join('\n').trim()
}
